import React from 'react';
import { AlertCircle, ArrowRight, Camera, Spline, TreeDeciduous } from 'lucide-react';
import { ITEMS_CON_ARBOL } from 'lib/arbolesCache';
import { ITEMS_CON_LINEA } from 'lib/lineasCache';
import { ESTADO_FILA, ESTADO_PILL } from 'lib/estadoUi';
import { CAMPO_TEXTO, esHallazgo, fotosTexto } from './comun';
import EstadoPill from './EstadoPill';
import FranjaFalla from './FranjaFalla';

const NEUTRO = 'bg-white text-foreground border-border';
export const MOTIVO_MINIMO = 5;

function Chip({ tipo, label, count }) {
  let tono;
  if (tipo === 'faltan') tono = count > 0 ? ESTADO_PILL.falla : NEUTRO;
  else tono = count > 0 ? ESTADO_PILL[tipo] : NEUTRO;
  return (
    <div data-chip={tipo} className={`flex items-baseline gap-1.5 rounded-full border px-3.5 py-2 ${tono}`}>
      <span className="text-lg font-bold tabular-nums">{count}</span>
      <span className="text-sm font-semibold">{label}</span>
    </div>
  );
}

// Resumen antes de guardar: conteos, los ítems del checklist con su estado (y
// un "Ir" en cada uno que falta), el detalle de los hallazgos, el inspector y
// lo que falta para guardar (con un botón que lleva al paso exacto).
// correccion: { motivo, onMotivo } en modo corrección (pide el motivo).
// arboles: árboles de la instalación, para mostrar los códigos elegidos.
export default function PasoResumen({
  items, resultados, detalles, comentarios, fotoBlobs, inspector, problemas, conteo, faltan, onIr, error,
  nombreInstalacion, correccion, arboles = [], lineas = [],
}) {
  const codigoDe = new Map(arboles.map((a) => [a.id, a.codigo]));
  const textoArboles = (ids) => {
    const nombres = ids.map((id) => codigoDe.get(id) || 'árbol fuera de la lista');
    return `${ids.length === 1 ? 'Árbol' : 'Árboles'}: ${nombres.join(', ')}`;
  };
  const codigoLinea = new Map(lineas.map((l) => [l.id, l.codigo]));
  const textoLineas = (ids) => {
    const nombres = ids.map((id) => codigoLinea.get(id) || 'cable fuera de la lista');
    return `${ids.length === 1 ? 'Cable' : 'Cables'}: ${nombres.join(', ')}`;
  };
  return (
    <div>
      <h2 tabIndex={-1} className="text-2xl font-bold leading-tight text-foreground focus:outline-none">
        Resumen
      </h2>

      <div className="mt-4 flex flex-wrap gap-2">
        <Chip tipo="aprobado" label="Aprobado" count={conteo.aprobado} />
        <Chip tipo="alerta" label="Alerta" count={conteo.alerta} />
        <Chip tipo="falla" label="Falla" count={conteo.falla} />
        <Chip tipo="faltan" label="faltan" count={faltan} />
      </div>

      {conteo.falla > 0 ? <FranjaFalla fallas={conteo.falla} nombreInstalacion={nombreInstalacion} /> : null}

      {error ? (
        <div role="alert" className={`mt-4 rounded-2xl border-2 p-4 text-base ${ESTADO_PILL.falla}`}>
          <p className="flex items-start gap-2 font-bold">
            <AlertCircle size={20} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </p>
          <p className="mt-1 text-foreground">
            Sus datos siguen aquí. Puede intentar guardar de nuevo; si el error se repite, avise a administración.
          </p>
        </div>
      ) : null}

      {problemas.length > 0 && (
        <div className="mt-4 rounded-2xl border-2 border-foreground/30 bg-card p-4">
          <p className="text-base font-bold text-foreground">Antes de guardar falta:</p>
          <ul className="mt-2 space-y-3">
            {problemas.map((p) => (
              <li key={p.id}>
                <p className="text-base text-foreground">{p.texto}</p>
                {p.boton ? (
                  <button
                    type="button"
                    onClick={() => onIr(p.paso)}
                    className="mt-1.5 inline-flex min-h-[48px] items-center gap-2 rounded-xl border border-primary/40 bg-primary/5 px-4 text-base font-semibold text-primary"
                  >
                    {p.boton}
                    <ArrowRight size={18} aria-hidden="true" />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {correccion ? (
        <div className="mt-6">
          <label htmlFor="motivo-correccion" className="block text-base font-bold text-foreground">
            Motivo de la corrección
          </label>
          <p className="mt-1 text-sm text-foreground">
            Queda en la inspección original al anularla. Al menos {MOTIVO_MINIMO} caracteres.
          </p>
          <textarea
            id="motivo-correccion"
            rows={3}
            value={correccion.motivo}
            onChange={(e) => correccion.onMotivo(e.target.value)}
            className={`mt-2 ${CAMPO_TEXTO}`}
          />
        </div>
      ) : null}

      <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-foreground">Ítems</h3>
      <ul aria-label="Ítems de la inspección" className="mt-2 space-y-2.5">
        {items.map((it, i) => {
          const v = resultados[it.key];
          const d = detalles[it.key] || {};
          const n = (d.fotoBlobs || []).length;
          const sinFoto = v === 'falla' && n === 0;
          const idsArb = d.arboles || [];
          const pideArbol = ITEMS_CON_ARBOL.includes(it.key) && esHallazgo(v);
          const sinArbol = pideArbol && arboles.length > 0 && idsArb.length === 0;
          const idsLin = d.lineas || [];
          const pideLinea = ITEMS_CON_LINEA.includes(it.key) && esHallazgo(v);
          const sinLinea = pideLinea && lineas.length > 0 && idsLin.length === 0;
          return (
            <li
              key={it.key}
              data-estado={v || 'sin-marcar'}
              className={`rounded-2xl p-3.5 ring-1 ${v ? `ring-border ${ESTADO_FILA[v]}` : 'bg-white ring-foreground/30'}`}
            >
              <div className="flex items-center justify-between gap-3">
                <p className="min-w-0 text-base font-bold text-foreground">
                  <span className="tabular-nums">{i + 1}.</span> {it.label}
                </p>
                <div className="flex shrink-0 items-center gap-2">
                  <EstadoPill estado={v} />
                  {!v ? (
                    <button
                      type="button"
                      aria-label={`Ir a ${it.label}`}
                      onClick={() => onIr(i)}
                      className="inline-flex min-h-[48px] items-center gap-1 rounded-xl border border-primary/40 bg-primary/5 px-3 text-base font-semibold text-primary"
                    >
                      Ir
                      <ArrowRight size={18} aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              </div>
              {esHallazgo(v) ? (
                <>
                  <p className="mt-1 text-base text-foreground">
                    {(d.nota || '').trim() || 'Sin descripción'}
                  </p>
                  <p className={`mt-1 flex items-center gap-1.5 text-sm ${sinFoto ? 'font-bold text-error' : 'text-foreground'}`}>
                    <Camera size={14} aria-hidden="true" />
                    {sinFoto ? 'Falla sin foto' : fotosTexto(n)}
                  </p>
                  {idsArb.length || sinArbol ? (
                    <p className={`mt-1 flex items-center gap-1.5 text-sm ${sinArbol ? 'font-bold text-error' : 'text-foreground'}`}>
                      <TreeDeciduous size={14} aria-hidden="true" />
                      {sinArbol ? 'Falta elegir el árbol' : textoArboles(idsArb)}
                    </p>
                  ) : null}
                  {idsLin.length || sinLinea ? (
                    <p className={`mt-1 flex items-center gap-1.5 text-sm ${sinLinea ? 'font-bold text-error' : 'text-foreground'}`}>
                      <Spline size={14} aria-hidden="true" />
                      {sinLinea ? 'Falta elegir el cable' : textoLineas(idsLin)}
                    </p>
                  ) : null}
                </>
              ) : null}
            </li>
          );
        })}
      </ul>

      <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-foreground">Observaciones</h3>
      <p className="mt-2 whitespace-pre-line text-base text-foreground">
        {comentarios.trim() || 'Sin comentarios.'}
      </p>
      <p className="mt-1 text-sm text-foreground">{fotosTexto(fotoBlobs.length)} generales</p>

      <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-foreground">Inspector</h3>
      <p className="mt-2 text-lg font-semibold text-foreground">{inspector || 'Sin indicar'}</p>
    </div>
  );
}
